import {useEffect, useState} from 'react';
import {UnsortableTable} from '../UnsortableTable';
import {listData} from '../../../constants/listData';
import {TenantProps} from '../../../constants/types';
import Error from '../Error';
import Spinner from '../Spinner';
import {Button} from '../Button';
import {sendGetRequest} from '../../../api/requests';
import ConfirmationPopup from '../popup/ConfirmationPopup';
import ListPopup from '../popup/ListPopup';
import {useTranslation} from 'react-i18next';
import {useSnackbarHook} from '../../../hooks/useSnackbar';

type AddToTenantProps = {
  id: string;
  name: string;
  entityKey: string;
  toastTitle: string;
}

const AddToTenant = ({id, name, entityKey, toastTitle}: AddToTenantProps) => {
  const {t} = useTranslation('tenants');
  const [data, setData] = useState<Nullable<TenantProps[]>>(null);
  const [error, setError] = useState('');
  const [isPopupShown, setIsPopupShown] = useState(false);
  const [isListPopupShown, setIsListPopupShown] = useState(false);
  const [tenantsToAdd, setTenantsToAdd] = useState<number[]>([]);
  const [tenantToRemove, setTenantToRemove] = useState({id: '', name: ''});
  const [spinnerText, setSpinnerText] = useState('');
  const {addSuccessToast, addErrorToast} = useSnackbarHook();

  const entity = listData[entityKey];

  useEffect(() => {
    (async () => {
      setSpinnerText(entity?.loadingText);
      try {
        const response = await sendGetRequest(entity?.fetchUrl(id));
        setData(response);
      } catch (error) {
        setError(error?.message);
      }
    })();
    return () => {
      setData(null);
      setError('');
    };
  }, []);

  const handleTenantRemove = async (tenantId: string, tenantName: string) => {
    setSpinnerText(entity?.spinnerTextRemove(tenantName));
    setData(null);
    setIsPopupShown(false);
    try {
      await entity?.remove(id, tenantId);
      addSuccessToast(t('toasts.removeFromTenantMessage', {toastTitle}));
      const response = await sendGetRequest(entity?.fetchUrl(id));
      setData(response);
    } catch (error) {
      addErrorToast(error?.message);
    }
  };

  const handleTenantAdd = async () => {
    setSpinnerText(entity?.spinnerTextAdd(name));
    setData(null);
    setIsListPopupShown(false);
    try {
      await entity?.add(id, tenantsToAdd);
      const toastEnding = tenantsToAdd.length > 1 ? t('toasts.tenants') : t('toasts.tenant');
      addSuccessToast(t('toasts.addToTenantMessage', {toastTitle, toastEnding}));
      setTenantsToAdd([]);
      const response = await sendGetRequest(entity?.fetchUrl(id));
      setData(response);
    } catch (error) {
      addErrorToast(error?.message);
    }
  };

  if (error) {
    return <Error message={error} />;
  }

  const tableContent = !data ?
    <Spinner loadingText={spinnerText} /> :
    <>
      <UnsortableTable
        entity={entity}
        content={data}
        emptyTableMsg={entity?.emptyMessage(name)}>
        {data.map(({id, name}) => (
          <td key={`removeBtn_${id}`} className="cell narrow-cell">
            <Button
              label="Remove"
              variant="text"
              size="small"
              onClick={() => {
                setTenantToRemove({id: `${id}`, name: name});
                setIsPopupShown(true);
              }}
            />
          </td>
        ))}
      </UnsortableTable>

      <ConfirmationPopup
        labels={{body: entity?.popupLabel(tenantToRemove.name, name)}}
        isOpen={isPopupShown}
        setIsOpen={setIsPopupShown}
        confirmableAction={
          () => handleTenantRemove(tenantToRemove.id, tenantToRemove.name)
        }
      />

      <ListPopup
        isOpen={isListPopupShown}
        setIsOpen={setIsListPopupShown}
        confirmableAction={handleTenantAdd}
        entityName={name}
        entityKey={entity?.popupEntityKey}
        entitiesToAdd={tenantsToAdd}
        setEntitiesToAdd={setTenantsToAdd}
        entitiesList={data}
      />
    </>;

  return (
    <div className="tab-content">
      <div className="tab-header-and-add-btn">
        <h3 className="tab-header">{entity?.header}</h3>

        {data &&
          <Button
            label={entity?.addButtonLabel}
            onClick={() => setIsListPopupShown(true)}
          />
        }
      </div>
      {tableContent}
    </div>
  );
};

export default AddToTenant;
